import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Space,
  Typography,
  Tag,
  message,
  Tabs,
  Statistic,
  Row,
  Col,
  Input,
  Empty,
  Descriptions,
  Badge,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { approvalService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_CONFIG = {
  pending: { color: 'processing', icon: <ClockCircleOutlined /> },
  approved: { color: 'success', icon: <CheckCircleOutlined /> },
  rejected: { color: 'error', icon: <CloseCircleOutlined /> },
};

const Approvals = () => {
  const [requests, setRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const { can } = usePermissions();

  const canApprove = can('approvals', 'approve');
  const canReject = can('approvals', 'reject');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRequests, pending, statsData] = await Promise.all([
        approvalService.getRequests(),
        approvalService.getPendingRequests(),
        approvalService.getStats(),
      ]);
      setRequests(allRequests);
      setPendingRequests(pending);
      setStats(statsData);
    } catch (error) {
      message.error('Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;

    try {
      if (actionType === 'approve') {
        await approvalService.approve(selectedRequest.id, actionNotes);
        message.success('Request approved');
      } else {
        await approvalService.reject(selectedRequest.id, actionNotes);
        message.success('Request rejected');
      }
      setActionModalVisible(false);
      setSelectedRequest(null);
      setActionNotes('');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setDetailModalVisible(true);
  };

  const openActionModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setActionNotes('');
    setActionModalVisible(true);
  };

  const columns = [
    {
      title: 'Entity',
      key: 'entity',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.entity_type.charAt(0).toUpperCase() + record.entity_type.slice(1)} #{record.entity_id}
          </Text>
          <Text type="secondary">{record.action}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = STATUS_CONFIG[status];
        return (
          <Tag color={config?.color} icon={config?.icon}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Requested By',
      key: 'requested_by',
      render: (_, record) => record.requested_by?.full_name || `User #${record.requested_by_user_id}`,
    },
    {
      title: 'Requested',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openDetailModal(record)}
          >
            View
          </Button>
          {record.status === 'pending' && canApprove && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => openActionModal(record, 'approve')}
            >
              Approve
            </Button>
          )}
          {record.status === 'pending' && canReject && (
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => openActionModal(record, 'reject')}
            >
              Reject
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'pending',
      label: (
        <Badge count={pendingRequests.length} offset={[10, 0]}>
          Pending
        </Badge>
      ),
      children: (
        <Table
          columns={columns}
          dataSource={pendingRequests}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty description="No pending approvals" /> }}
        />
      ),
    },
    {
      key: 'all',
      label: 'All Requests',
      children: (
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          loading={loading}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Stats */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pending"
                value={stats.pending}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Approved"
                value={stats.approved}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Rejected"
                value={stats.rejected}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Total" value={stats.total} />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>Approval Requests</Title>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Request Details"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRequest(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          selectedRequest?.status === 'pending' && canApprove && (
            <Button
              key="approve"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setDetailModalVisible(false);
                openActionModal(selectedRequest, 'approve');
              }}
            >
              Approve
            </Button>
          ),
          selectedRequest?.status === 'pending' && canReject && (
            <Button
              key="reject"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setDetailModalVisible(false);
                openActionModal(selectedRequest, 'reject');
              }}
            >
              Reject
            </Button>
          ),
        ].filter(Boolean)}
        width={600}
      >
        {selectedRequest && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Entity">
              {selectedRequest.entity_type} #{selectedRequest.entity_id}
            </Descriptions.Item>
            <Descriptions.Item label="Action">{selectedRequest.action}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_CONFIG[selectedRequest.status]?.color}>
                {selectedRequest.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Requested By">
              {selectedRequest.requested_by?.full_name || `User #${selectedRequest.requested_by_user_id}`}
            </Descriptions.Item>
            <Descriptions.Item label="Requested At">
              {dayjs(selectedRequest.created_at).format('MMM D, YYYY h:mm A')}
            </Descriptions.Item>
            {selectedRequest.requested_changes && (
              <Descriptions.Item label="Requested Changes">
                <pre style={{ margin: 0, fontSize: 12 }}>
                  {JSON.stringify(selectedRequest.requested_changes, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedRequest.approved_by && (
              <>
                <Descriptions.Item label="Processed By">
                  {selectedRequest.approved_by?.full_name || `User #${selectedRequest.approved_by_user_id}`}
                </Descriptions.Item>
                <Descriptions.Item label="Completed At">
                  {dayjs(selectedRequest.completed_at).format('MMM D, YYYY h:mm A')}
                </Descriptions.Item>
              </>
            )}
            {selectedRequest.approval_notes && (
              <Descriptions.Item label="Notes">{selectedRequest.approval_notes}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Action Modal */}
      <Modal
        title={actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          setSelectedRequest(null);
          setActionNotes('');
        }}
        onOk={handleAction}
        okText={actionType === 'approve' ? 'Approve' : 'Reject'}
        okType={actionType === 'approve' ? 'primary' : 'danger'}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {actionType === 'approve'
              ? 'Are you sure you want to approve this request?'
              : 'Are you sure you want to reject this request?'}
          </Text>
          <TextArea
            placeholder="Add notes (optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            rows={3}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default Approvals;
